'use client';

import cn from 'classnames';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { Burger } from '@/components/Icons/Burger';
import { Link } from '@/components/Link';
import { Wrapper } from '@/components/Wrapper';
import { useHeaderPosition } from '@/hooks/useHeaderPosition';
import { useWindowWidth } from '@/hooks/useWindowWidth';

import { SubHeader } from '../SubHeader';
import s from './Header.module.scss';

const Logo = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="129"
      height="24"
      viewBox="0 0 129 24"
      fill="none"
    >
      <path
        d="M11.5155 13.4374H21.4529V19.7638L11.5137 23.9464V13.4356L11.5155 13.4374ZM21.4529 11.9726V5.64629L11.5155 1.46191V11.9672L21.4529 11.9726ZM0.00585938 5.64629V19.7656L9.94504 23.9482V1.46191L0.00585938 5.64629Z"
        fill="#26d07c"
      />
      <path
        d="M35.5359 5.33033C30.8208 5.33033 28.2812 8.00101 28.2812 12.7123C28.2812 17.4236 30.8208 20.0942 35.4055 20.0942C38.4179 20.0942 40.8524 18.5751 41.8215 15.4862H38.6787C38.0755 16.5327 37.2115 17.1882 35.4055 17.1882C32.7083 17.1882 31.6342 15.7487 31.6342 12.7376V12.6598C31.6342 9.64869 32.7083 8.2364 35.5359 8.2364C37.2115 8.2364 38.0755 8.81218 38.6787 9.80803H41.8215C40.8524 6.8241 38.5483 5.33214 35.5359 5.33214V5.33033ZM68.5361 14.0485C68.5361 16.1434 69.5831 17.19 71.6789 17.19C73.7747 17.19 74.8217 16.1434 74.8217 14.0485V5.64357H78.1221V19.781H75.0318V17.6861C74.1678 19.1781 72.7531 20.0942 70.3964 20.0942C67.0435 20.0942 65.2376 18.2619 65.2376 14.5972V5.64357H68.5379V14.0467L68.5361 14.0485ZM119.161 14.0485C119.161 16.1434 120.208 17.19 122.304 17.19C124.4 17.19 125.447 16.1434 125.447 14.0485V5.64357H128.747V19.781H125.657V17.6861C124.793 19.1781 123.378 20.0942 121.022 20.0942C117.669 20.0942 115.863 18.2619 115.863 14.5972V5.64357H119.163V14.0467L119.161 14.0485ZM87.5505 17.19C90.3799 17.19 91.4794 15.7505 91.4794 12.7394V12.6616C91.4794 9.6505 90.3799 8.26356 87.5505 8.26356C84.721 8.26356 83.6215 9.6505 83.6215 12.6616V12.7394C83.6215 15.7505 84.721 17.19 87.5505 17.19ZM94.6729 0.146484V19.781H91.5301V17.6861C90.6389 19.1002 89.2785 20.0942 86.9473 20.0942C82.4948 20.0942 80.2686 17.0053 80.2686 12.9205C80.2686 8.44462 82.4948 5.35568 86.9473 5.35568C88.8855 5.35568 90.744 6.29902 91.3725 7.5556V0.146484H94.6729ZM43.104 19.781H46.4044V0.146484H43.104V19.781ZM55.8327 20.096C51.3278 20.096 48.5002 17.295 48.5002 12.7141C48.5002 8.13319 51.3296 5.33214 55.8327 5.33214C60.3359 5.33214 63.1653 8.13319 63.1653 12.7141C63.1653 17.295 60.3359 20.096 55.8327 20.096ZM51.8513 12.7394C51.8513 15.7505 52.9508 17.19 55.8327 17.19C58.7147 17.19 59.8142 15.7505 59.8142 12.7394V12.6616C59.8142 9.6505 58.7147 8.23821 55.8327 8.23821C52.9508 8.23821 51.8513 9.65231 51.8513 12.6616V12.7394ZM112.638 5.64538H114.211V8.78683H111.854C109.758 8.78683 109.052 9.75552 109.052 11.3253V19.781H105.751V5.61822H108.894V8.2364C109.523 6.42938 110.78 5.64538 112.64 5.64538H112.638ZM100.198 20.096C101.638 20.096 102.554 19.2324 102.554 17.7658C102.554 16.2991 101.638 15.4355 100.198 15.4355C98.7576 15.4355 97.8411 16.2991 97.8411 17.7658C97.8411 19.2324 98.7576 20.096 100.198 20.096Z"
        fill="#222222"
      />
    </svg>
  );
};

const MOCK_ITEMS = [
  {
    id: 'header-company',
    title: 'О компании',
    link: '/',
  },
  {
    id: 'header-vacancies',
    title: 'Вакансии',
    link: '/vacancies',
  },
  {
    id: 'header-bootcamp',
    title: 'Стажировки',
    link: '/bootcamp',
  },
];

interface MenuProps {
  onClickOnMenuItem?(): void;
}

const Menu = ({ onClickOnMenuItem }: MenuProps) => {
  const pathname = usePathname();

  return (
    <nav className={s.menu}>
      {MOCK_ITEMS.map(({ id, title, link }, index) => {
        return (
          <Link
            onClick={onClickOnMenuItem}
            key={index}
            href={link}
            className={cn(s.item, 'link', {
              [s.active]: pathname === link,
            })}
            analyticsOptions={{
              action: link,
              clickZone: 'header',
              clickElement: 'textclick',
              clickContent: title,
              uniqueId: id,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-header',
              transitionTypeMetrics: 'internal',
              transitionUrl: `/career/${link}`,
            }}
          >
            {title}
          </Link>
        );
      })}
    </nav>
  );
};

const Header = () => {
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);

  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();
  const {
    showHeader,
    headerPosition,
    showSubheader,
    setShowSubheader,
    headerHeight,
  } = useHeaderPosition(isBurgerOpen);

  const onBurgerClick = () => setIsBurgerOpen((prev) => !prev);
  const onClickOnMenuItem = () => setIsBurgerOpen(false);

  return (
    <header
      className={cn(s.root, {
        [s.shadow]: headerPosition > 50 && showHeader,
      })}
      style={{
        transform: `translateY(-${!showHeader ? headerHeight : 0}px)`,
      }}
      data-attr="layout-header"
    >
      <SubHeader
        setShowSubheader={setShowSubheader}
        showSubheader={showSubheader}
      />
      <div className={s.padding}>
        <Wrapper className={s.wrapper}>
          <div className={s.logos}>
            <Link
              className={s.link}
              href="https://cloud.ru"
              prefetch={false}
              analyticsOptions={{
                action: 'https://cloud.ru',
                clickZone: 'header',
                clickElement: 'textclick',
                clickContent: 'logo',
                uniqueId: 'cloud-logo',
                transitionType: 'inside-link',
                clickZoneCategory: 'site-header',
                transitionTypeMetrics: 'internal',
                transitionUrl: 'https://cloud.ru',
              }}
            >
              <Logo />
            </Link>
            <Link
              href="/"
              className={s.logo}
              analyticsOptions={{
                action: '/career',
                clickZone: 'header',
                clickElement: 'textclick',
                clickContent: 'career',
                uniqueId: 'career-logo',
                transitionType: 'inside-link',
                clickZoneCategory: 'site-header',
                transitionTypeMetrics: 'internal',
                transitionUrl: '/career',
              }}
            >
              <span className={s.logoText}>/ CAREER</span>
            </Link>
          </div>

          {windowWidth && windowWidth > MD && <Menu />}
          {windowWidth && windowWidth <= MD && (
            <div className={s.burgerWrapper} onClick={onBurgerClick}>
              <Burger className={s.burger} />
            </div>
          )}
        </Wrapper>
      </div>
      {isBurgerOpen && (
        <div
          style={{
            transform: `top: ${!showHeader && !isBurgerOpen ? headerHeight : 0}px)`,
          }}
        >
          <Menu onClickOnMenuItem={onClickOnMenuItem} />
        </div>
      )}
    </header>
  );
};

export { Header };
